import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SiteNav from '../components/SiteNav';
import PageMeta from '../components/PageMeta';
import LanguageSuggestionBanner from '../components/LanguageSuggestionBanner';
import { trackEvent } from '../components/AnalyticsTracker';
import ScrollTopButton from '../components/ScrollTopButton';
import {
  Brain, ShieldCheck, LayoutDashboard,
  LifeBuoy, Eye, Users, ArrowRight, Quote, Sparkles, FileDown, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INK = '#1A1209';
const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

const AVAS = [
  { img: '/clare-ava.png', name: 'Maya', role: 'General Confidence & Communication', desc: 'General confidence & communication practice partner. Helps students find their voice in low-pressure, everyday scenarios.', tint: '#F0E9FF', color: PURPLE },
  { img: '/avas/rebecca.png', name: 'Rebecca', role: 'Science & Technology Tutor', desc: 'Structured, logical guidance focusing on academic readiness, logic, and problem-solving.', tint: '#FFF5EA', color: ORANGE },
  { img: '/avas/neo.jpg', name: 'Neo', role: 'Customisable Challenge Tutor', desc: 'Custom appearance, voice, and name adapted to the student\u2019s preference for real-world challenge rehearsal.', tint: '#EAF7EF', color: '#0F8A4B' },
  { img: '/avas/leo.jpg', name: 'Leo', role: 'Relationship Communication Tutor', desc: 'Dating, crushes and relationship communication — rehearsing kindness, boundaries and difficult messages safely.', tint: '#FDF2F4', color: '#E11D48' },
  { img: '/avas/marcus.jpg', name: 'Marcus', role: 'Career & Authority Negotiation Tutor', desc: 'Job interviews, university admissions, and speaking up respectfully with teachers and authority figures.', tint: '#EFF4FF', color: '#1D4ED8' },
  { img: '/avas/chloe.jpg', name: 'Chloe', role: 'Social Dynamics & Conflict Tutor', desc: 'Peer dynamics, group projects and conflict resolution — standing your ground calmly and kindly.', tint: '#EDFAF3', color: '#059669' },
  { img: '/avas/ethan.jpg', name: 'Ethan', role: 'Presentation & Voice Confidence Tutor', desc: 'Public speaking, class presentations and voice confidence — pace, filler words, tone and structure.', tint: '#FEF7EC', color: '#B45309' },
  { img: '/avas/sarah.jpg', name: 'Sarah', role: 'Organization & Boundaries Tutor', desc: 'Workload, time management and beating procrastination — plus saying no and setting healthy boundaries.', tint: '#ECFBF8', color: '#0D9488' },
  { img: '/avas/petra.jpg', name: 'Petra', role: 'Digital Life & Online Safety Tutor', desc: 'Digital literacy, cyberbullying and group chat drama — staying safe, kind and in control online.', tint: '#FDF0F7', color: '#DB2777' },
];

const RECEIVE = [
  { icon: Brain, title: 'Personalised Mentorship', desc: 'The 22-question OCEAN assessment tailors scenarios to each student\u2019s unique communication style and confidence gaps.' },
  { icon: ShieldCheck, title: 'Safe by Design', desc: 'NVIDIA NeMo Guardrails block toxic inputs, off-topic chat, and unvetted content, keeping all interactions educational.' },
  { icon: LayoutDashboard, title: 'Pastoral Insights & Safeguarding', desc: 'Admin dashboards give pastoral leaders visibility into engagement metrics without violating individual student privacy.' },
  { icon: LifeBuoy, title: 'Human-Escalation Protocol', desc: 'Hardcoded sentiment detection detects emotional distress, triggering a 3-step check and generating a personalised introduction script for local school counsellors or helplines.' },
  { icon: Users, title: 'Parent-Gated Compliance', desc: 'Zero administrative burden on teachers to gather consent forms. Our automated system handles direct parent opt-ins, giving families total control over account activation, reward minting, and independent monthly progress tracking.' },
];

const SAFETY = [
  { icon: ShieldCheck, title: 'COPPA & GDPR Compliant', desc: 'Fully compliant with global student privacy regulations.' },
  { icon: Eye, title: 'Zero Data Selling', desc: 'Student conversation data is strictly confidential and never used for advertising or AI training.' },
  { icon: Sparkles, title: 'SOC 2 Readiness Architecture', desc: 'Hardcoded enterprise security protocols and access controls.' },
  { icon: Users, title: 'Parent & School Governance', desc: 'Institutional admin controls over challenge access and milestone rewards.' },
];

const JOURNEY = [
  ['Activation', 'School assigns the licence → Parent approves via email/SMS link → Teen completes the 22-question OCEAN profiling assessment with Maya.'],
  ['Meet your Challenge Friend', 'Neo guides teens through real-world challenges. Teens customise Neo\u2019s image, name, voice, and backstory — making it uniquely theirs.'],
  ['Meet Rebecca', 'The Science & Technology Tutor. Patient, Socratic, and passionate about STEM.'],
  ['A growing library of specialist AvAs', 'HiBFF creates additional AvAs for specific functions — building a library of specialist AvAs around teens\u2019 needs. Teens can customise any AvA from the library with a new image, voice, and language, give it a name, and engage on that specific subject.'],
  ['Practise by text or voice', 'Whichever feels most comfortable. Voice sessions make the practice feel real.'],
  ['Real-world challenges', 'Starting a conversation. Handling an awkward moment. Saying something kind to a stranger. Every completion earns rewards.'],
  ['Parent-Handed Rewards & Reports', 'Parent receives an independent monthly progress report and holds exclusive authority to approve and mint earned challenge NFTs on behalf of their teen.'],
];


const CohortCalculator = () => {
  return (
    <div className="mt-10 max-w-xl mx-auto text-left bg-white border rounded-2xl p-6 shadow-sm" style={{ borderColor: '#F3E8D9' }} data-testid="cohort-calculator">
      <p className="text-sm font-bold mb-1" style={{ color: INK }}>Your school&apos;s rate</p>
      <p className="text-xs text-gray-500 mb-4">Every school&apos;s circumstances are different, so we quote each cohort individually.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href="mailto:schools@hibff.com?subject=HiBFF%20Schools%20—%20Special%20Pricing%20Enquiry"
          className="block text-center rounded-xl px-4 py-3 text-sm font-bold text-white"
          style={{ backgroundColor: PURPLE }}
          data-testid="schools-pricing-enquiry-btn"
        >
          Call for Special Pricing
        </a>
        <a
          href="/school/register"
          className="block text-center rounded-xl px-4 py-3 text-sm font-bold border"
          style={{ color: PURPLE, borderColor: PURPLE }}
          data-testid="schools-trial-btn"
        >
          Start a 14-day free trial
        </a>
      </div>
      <p className="text-[11px] text-gray-400 mt-3 text-center">schools@hibff.com</p>
    </div>
  );
};

const INCLUDES = [
  'All 9 specialist AvA tutors (Maya, Rebecca, Neo + 6 topic-locked specialists)',
  '22-Question OCEAN personalisation assessment',
  'Unlimited text practice & guided challenges',
  'NVIDIA NeMo Guardrails & multi-layer security',
  'Human-Escalation Safety Net (local expert off-ramp & introduction script)',
  'Safeguarding & pastoral insights dashboard',
  'COPPA, GDPR & SOC 2 readiness architecture',
];

const BrochureForm = () => {
  const [form, setForm] = React.useState({ name: '', email: '', school_name: '' });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const valid = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && form.school_name.trim().length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/schools/brochure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error('request failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'HiBFF-Schools-Prospectus.pdf';
      a.click();
      URL.revokeObjectURL(url);
      setMsg({ ok: true, text: 'Your prospectus is downloading — thank you!' });
      try { trackEvent('school_lead'); } catch (_e) { /* noop */ }
    } catch (_e) {
      setMsg({ ok: false, text: 'Something went wrong — please try again in a moment.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="brochure-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="text" required placeholder="First name *" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#7B4FD8]"
          style={{ borderColor: '#E8DECF' }} data-testid="brochure-name-input"
        />
        <input
          type="email" required placeholder="Work email *" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#7B4FD8]"
          style={{ borderColor: '#E8DECF' }} data-testid="brochure-email-input"
        />
      </div>
      <input
        type="text" required placeholder="School / institution name *" value={form.school_name}
        onChange={(e) => setForm({ ...form, school_name: e.target.value })}
        className="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#7B4FD8]"
        style={{ borderColor: '#E8DECF' }} data-testid="brochure-school-input"
      />
      <button
        type="submit" disabled={!valid || busy}
        className="w-full sm:w-auto px-8 py-3 rounded-xl font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ backgroundColor: '#F5831F' }} data-testid="brochure-download-btn"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
        {busy ? 'Preparing your PDF…' : 'Download School Prospectus'}
      </button>
      {msg && (
        <p className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-600'}`} data-testid="brochure-form-msg">{msg.text}</p>
      )}
    </form>
  );
};

export default function SchoolsLanding() {
  const { dir } = useLanguage();
  const navigate = useNavigate();
  useEffect(() => { document.title = 'HiBFF for Education — Empowering Students to Navigate Real Life with Confidence'; window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFDF9' }} data-testid="schools-landing" dir={dir}>
      <LanguageSuggestionBanner />
      {/* Header — canonical shared HiBFF menu (identical on all pages) */}
      <PageMeta
        title="HiBFF for Education — Social Confidence Programmes for Schools & Universities"
        description="Give your students a safe space to practise real-world conversations. HiBFF school programmes include safeguarding controls, cohort pricing and teacher dashboards."
        path="/schools"
        slug="schools"
      />
      <SiteNav current="schools" mode="sticky" />

      {/* Hero */}
      <section className="pt-20 pb-14 px-4 text-center" data-testid="schools-hero">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl mx-auto" style={{ fontFamily: 'Georgia, serif', color: INK }}>
          HiBFF for Education
        </h1>
        <p className="text-xl sm:text-2xl font-semibold mt-4 max-w-3xl mx-auto" style={{ fontFamily: 'Georgia, serif', color: INK }}>
          Empowering Students to Navigate Real Life with Confidence
        </p>
        <p className="max-w-3xl mx-auto mt-8 text-base sm:text-lg leading-relaxed text-gray-700" data-testid="schools-hero-sub">
          A safe, structured, AI-powered space for students — from secondary schools and academies to universities — to practice communication, build social resilience,
          and rehearse difficult conversations before they happen in the real world.
        </p>
      </section>

      {/* Pricing — the centrepiece */}
      <section className="py-16 px-4" style={{ backgroundColor: '#FFF5EA' }} data-testid="schools-pricing">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-6" style={{ color: ORANGE }}>Schools, Academies &amp; Universities Tier</p>
          <p className="text-5xl sm:text-6xl font-bold" style={{ fontFamily: 'Georgia, serif', color: INK }} data-testid="schools-price">
            Call for Special Pricing
          </p>
          <p className="text-lg mt-2" style={{ color: '#6B6257' }}>every school and university&apos;s rate is quoted individually</p>
          <p className="text-sm mt-2 font-semibold" style={{ color: PURPLE }} data-testid="schools-discount-line">
            14-Day Free Trial Available — Up to 10 Students
          </p>
          <p className="text-sm mt-4 max-w-xl mx-auto" style={{ color: '#8A8073' }} data-testid="schools-budget-line">
            <strong style={{ color: INK }}>Built for Education Budgets:</strong> Scalable social-emotional practice designed
            to fit within Pastoral Care and Pupil Premium allocations — and university student wellbeing budgets.
          </p>
          <p className="text-xs mt-3" style={{ color: '#8A8073' }} data-testid="schools-benchmark-line">
            Email <a href="mailto:schools@hibff.com" style={{ color: PURPLE, fontWeight: 600 }}>schools@hibff.com</a> and we&apos;ll come back with your institution&apos;s special rate — volume agreements available for district-wide and multi-campus rollouts
          </p>
          <div className="mt-10 max-w-xl mx-auto text-left bg-white border rounded-2xl p-6 shadow-sm" style={{ borderColor: '#F3E8D9' }} data-testid="schools-includes">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: PURPLE }}>Every licence includes</p>
            <ul className="space-y-2.5">
              {INCLUDES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: '#4A4238' }}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: ORANGE }} />{f}
                </li>
              ))}
            </ul>
          </div>

          {/* Cohort cost calculator */}
          <CohortCalculator />
        </div>
      </section>

      {/* Trial */}
      <section className="py-16 px-4" data-testid="schools-trial">
        <div className="max-w-3xl mx-auto text-center bg-white rounded-3xl border-2 p-10" style={{ borderColor: ORANGE }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: ORANGE }}>Try HiBFF in your school</p>
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Georgia, serif', color: INK }}>
            14-Day Free Trial. Up to 10 Students. No Obligation.
          </h2>
          <p className="text-gray-600 mt-4 max-w-xl mx-auto text-sm sm:text-base">
            Deploy HiBFF with a small pilot cohort for 14 days. Experience how our structured
            social challenges fit into your school&apos;s pastoral care, anti-bullying, and communication curriculum.
            At the end of the trial, licence the full school — or walk away, no questions asked.
          </p>
          <button onClick={() => navigate('/school/register')} className="mt-8 px-8 py-3.5 text-white font-semibold rounded-xl inline-flex items-center gap-2" style={{ backgroundColor: PURPLE }} data-testid="schools-trial-cta">
            Request Your 14-Day Free Trial <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Meet the AvAs */}
      <section className="py-16 px-4 bg-white border-y" style={{ borderColor: '#F3E8D9' }} data-testid="schools-avas">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3" style={{ fontFamily: 'Georgia, serif', color: INK }}>
            Meet the AvA Team — Specialised Tutors for Real-World Growth
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-sm sm:text-base">
            Every student&apos;s journey begins with Maya, who guides them through the 22-question OCEAN
            personalisation assessment. Around her: six specialist, topic-locked tutors — each an expert in one
            high-stakes teen scenario, and nothing else.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {AVAS.map(({ img, name, role, desc, tint, color }) => (
              <div key={name} className="rounded-2xl p-6 border text-center" style={{ backgroundColor: tint, borderColor: '#F3E8D9' }} data-testid={`schools-ava-${name.toLowerCase()}`}>
                <img src={img} alt={name} className="w-20 h-20 rounded-full object-cover mx-auto mb-4 shadow-md" style={{ border: `3px solid ${color}` }} />
                <p className="font-bold text-lg" style={{ color: INK }}>{name}</p>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color }}>{role}</p>
                <p className="text-sm text-gray-700 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What schools receive */}
      <section className="py-16 px-4" data-testid="schools-receive">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Georgia, serif', color: INK }}>
            What schools receive
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="schools-receive-grid">
            {RECEIVE.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4 p-5 rounded-2xl bg-white border" style={{ borderColor: '#F3E8D9' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F0E9FF' }}>
                  <Icon size={20} style={{ color: PURPLE }} />
                </div>
                <div>
                  <p className="font-semibold" style={{ color: INK }}>{title}</p>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* The School–Parent Governance Loop — the Teen-Parent-School triad */}
          <div className="mt-10 rounded-3xl border-2 p-8 sm:p-10 bg-white" style={{ borderColor: PURPLE + '55' }} data-testid="schools-governance-loop">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Georgia, serif', color: INK }}>
              The School–Parent Governance Loop
            </h3>
            <p className="text-center text-sm text-gray-600 mb-8 max-w-2xl mx-auto">
              Parent-gated onboarding and governance — simple, legally compliant implementation from day one.
            </p>
            <ol className="space-y-5 max-w-3xl mx-auto">
              {[
                ['School License Provisioning', 'The school purchases a bulk seat license cohort — terms agreed individually with each school.'],
                ['Parental Verification & Opt-In', 'An automated invitation is sent to the parent/guardian. The teen only gains system access once the parent explicitly approves the account setup.'],
                ['Parental Rewards Custody', 'As the teen completes challenges, earned NFT achievements are sent to the 180-Day Vault. The parent retains sole authority to mint these on behalf of the teen via their private dashboard.'],
                ['Independent, Uninfluenced Monthly Reporting', 'Parents receive an automated monthly progress report tracking OCEAN growth metrics, challenge completions, and social milestones directly from the system — completely independent of school editorial control or influence.'],
              ].map(([title, desc], i) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm flex-shrink-0" style={{ backgroundColor: PURPLE }}>{i + 1}</span>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: INK }}>{title}</p>
                    <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {[
                ['Zero School Liability', 'The parent-confirmed opt-in handles GDPR/COPPA consent — the school never acts as primary data controller for home practice.'],
                ['School–Home Trust', 'The monthly report goes straight from the system to the parent — never filtered or edited by the school.'],
                ['Incentive Control', 'Teachers never manage rewards or blockchain mechanics — the parent holds the vault keys.'],
              ].map(([title, desc]) => (
                <div key={title} className="p-4 rounded-2xl text-center" style={{ backgroundColor: '#F0E9FF' }}>
                  <p className="font-bold text-sm" style={{ color: PURPLE }}>{title}</p>
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 px-4 bg-white border-y" style={{ borderColor: '#F3E8D9' }} data-testid="schools-safety">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3" style={{ fontFamily: 'Georgia, serif', color: INK }}>
            The Safety Stack — Built for Schools, Trusted by Parents
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-sm sm:text-base">
            A multi-layered safety architecture that ensures every interaction is age-appropriate,
            <br />
            secure, and never left to chance.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SAFETY.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-5 rounded-2xl border" style={{ backgroundColor: '#FFFDF9', borderColor: '#F3E8D9' }}>
                <Icon size={20} style={{ color: ORANGE }} className="mb-3" />
                <p className="font-semibold text-sm" style={{ color: INK }}>{title}</p>
                <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border-2 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" style={{ backgroundColor: '#F0E9FF', borderColor: PURPLE + '55' }} data-testid="schools-whitepaper">
            <FileDown size={32} style={{ color: PURPLE }} className="shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-base sm:text-lg" style={{ fontFamily: 'Georgia, serif', color: INK }}>
                Published White Paper — The Warm Handover
              </p>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                Our published safety standard for AI conversations with young people — how HiBFF hands a struggling
                teen from AI to a trusted human, every time. Read the full published paper.
              </p>
            </div>
            <a
              href="/warm-handover?src=education" target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 text-white font-semibold rounded-xl inline-flex items-center gap-2 shrink-0"
              style={{ backgroundColor: PURPLE }}
              data-testid="schools-whitepaper-btn"
            >
              Read the White Paper (PDF) <ArrowRight size={16} />
            </a>
          </div>
          <p className="text-sm font-bold flex items-center justify-center gap-1.5 mt-8" style={{ color: INK }} data-testid="schools-data-pledge">
            <ShieldCheck size={15} style={{ color: '#059669' }} />
            Your data belongs to you. No advertising.{' '}
            <a href="/privacy" className="underline decoration-2 underline-offset-2 font-semibold" style={{ color: PURPLE }} data-testid="schools-data-pledge-link">Our policy</a>
          </p>
        </div>
      </section>

      {/* A teen's journey */}
      <section className="py-16 px-4" data-testid="schools-journey">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Georgia, serif', color: INK }}>
            How it works — a teen&apos;s journey
          </h2>
          <ol className="space-y-6">
            {JOURNEY.map(([title, desc], i) => (
              <li key={title} className="flex items-start gap-5">
                <span className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm flex-shrink-0" style={{ backgroundColor: PURPLE }}>{i + 1}</span>
                <div>
                  <p className="font-semibold" style={{ color: INK }}>{title}</p>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Get started */}
      <section className="py-16 px-4 text-center" style={{ backgroundColor: '#F0E9FF' }} data-testid="schools-cta">
        <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Georgia, serif', color: INK }}>Get Started with HiBFF for Education</h2>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={() => navigate('/school/register')} className="px-8 py-3.5 text-white font-semibold rounded-xl inline-flex items-center gap-2" style={{ backgroundColor: PURPLE }} data-testid="schools-cta-trial">
            Request Your 14-Day Free Trial <ArrowRight size={16} />
          </button>
          <button onClick={() => navigate('/contact')} className="px-8 py-3.5 bg-white border border-gray-300 font-semibold rounded-xl" style={{ color: INK }} data-testid="schools-cta-contact">
            Contact Sales Team
          </button>
        </div>
      </section>

      {/* Brochure download — gated lead capture */}
      <section className="py-16 px-4" data-testid="schools-brochure">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl border p-8 sm:p-10 shadow-sm" style={{ borderColor: '#F3E8D9' }}>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FFF5EA' }}>
              <FileDown size={22} style={{ color: ORANGE }} />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Georgia, serif', color: INK }}>
                Download the HiBFF for Education Prospectus
              </h2>
              <p className="text-sm text-gray-600 mt-1.5">
                Get a detailed PDF breakdown of our curriculum integration, safeguarding protocols,
                and 40% institutional pricing model. All three fields are required.
              </p>
            </div>
          </div>
          <BrochureForm />
        </div>
      </section>

      {/* Founder's promise */}
      <section className="py-20 px-4 bg-white border-t" style={{ borderColor: '#F3E8D9' }} data-testid="schools-founder-promise">
        <div className="max-w-3xl mx-auto pl-6 border-l-4" style={{ borderColor: ORANGE }}>
          <Quote size={32} style={{ color: ORANGE }} className="mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-8" style={{ fontFamily: 'Georgia, serif', color: INK }}>The Founder&apos;s Promise</h2>
          <div className="space-y-5 leading-relaxed text-base" style={{ fontFamily: 'Georgia, serif', color: '#4A4238' }}>
            <p>I built this because I deleted everything. I launched HiBFF twice before this version. Each time, I watched it
            drift toward something I wasn&apos;t proud of — AI companions, digital relationships, more reasons to stay on a screen.
            So, I deleted it. The website. The AWS stack. The press campaign. Everything.</p>
            <p>Then I spent 100 hours a week building something I could actually stand behind. HiBFF is built on a simple belief:
            the world changed after Covid, and an entire generation of teenagers lost the years when social confidence is supposed
            to form naturally. I want to give that back to them — not through more screen time, but through practice. Real
            challenges. Real growth. And ultimately, real human connection.</p>
            <p>I know it works because I did it myself. Every single day, walking through London, I would stop a stranger and say
            something genuinely kind — then walk away without waiting for anything in return. It changed how I felt about myself.
            It changed how they started their day. That is what HiBFF is.</p>
            <p className="font-semibold" style={{ color: INK }}>— Robert Parson, Founder &amp; CEO</p>
          </div>
        </div>
      </section>

      {/* Footer — full site footer */}
      <footer className="py-10 px-4 bg-gray-900 text-white" data-testid="schools-footer">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/register" className="hover:text-white">Get Started</a></li>
                <li><a href="/pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/school/register" className="hover:text-white">School Registration</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/" className="hover:text-white">Home</a></li>
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/investor-portal" className="hover:text-white">Investor Portal</a></li>
                <li><a href="/partners" className="hover:text-white">Partners</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">For Schools</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/teen-safety" className="hover:text-white">Teen Safety &amp; Privacy</a></li>
                <li><a href="/trust-safety" className="hover:text-white">Trust &amp; Safety — Live Numbers</a></li>
                <li><a href="/docs" className="hover:text-white">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Legal &amp; Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white">Terms &amp; Conditions</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy &amp; Data Safety Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-white">Refund Policy</a></li>
                <li><a href="mailto:schools@hibff.com" className="hover:text-white">schools@hibff.com</a></li>
                <li><a href="/contact" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-gray-800 text-center text-xs text-gray-500">
            <p className="font-semibold text-gray-400 mb-1">Built for schools. Backed by science. Grounded in courage.</p>
            <p>HiBFF — Hi Brave Friends Forever · School pricing quoted individually per school — contact schools@hibff.com</p>
          </div>
        </div>
      </footer>

      <ScrollTopButton />
    </div>
  );
}
